/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { combineEpics } from 'redux-observable';
import { empty, from, of } from 'rxjs';
import { ajax, AjaxResponse } from 'rxjs/ajax';
import { catchError, debounceTime, exhaustMap, expand, filter, map, mergeMap, pluck, scan, switchMap, throttleTime, defaultIfEmpty } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import config from '../../config';
import { createEpic, getCommonHeaders } from '../../utils/epics';
import { SYNC_BACKOFF } from '../../utils/time';
import { CONTENT_TYPE_HEADER } from '../../utils/types';
import { redirect } from '../session';
import { FollowUp, FollowUpData, ImmediateFollowUp, openFollowUpEditor, RemoteFollowUpData, StandardFollowUp, submitFollowUp, syncFollowUps, throttledSyncFollowUps, getFollowUpsForFacility } from './actions';
import { nearestDeadline } from './reducers';
import moment from 'moment';

const MAX_FOLLOWUPS_TO_REQUEST_AT_ONCE = 2000;

export const followUpSortComparator = (a: FollowUpData, b: FollowUpData) => {
  const aDue = a.due ?? nearestDeadline(a);
  const bDue = b.due ?? nearestDeadline(b);

  return aDue.localeCompare(bDue);
};

/**
 * Tag each action plan with it's type ('mcap' | 'cip' | 'iap') so that they can be discriminated throughout the UI.
 * todo just use a class
 */
function prepareActionPlans(res: { results: RemoteFollowUpData[], actionPlanType: string }) {
  const { results, actionPlanType } = res;

  const allPlansByType: FollowUp[] = (results as RemoteFollowUpData[]).map((ap) =>
    actionPlanType === 'iaps'
      ? ({ data: ap, type: 'iap' } as ImmediateFollowUp)
      : ({ data: ap, type: actionPlanType.substr(0, actionPlanType.length - 1) } as StandardFollowUp)
  )

  return allPlansByType;
}

/**
 * Action plans have to be fetched one type at a time ('mcaps' | 'cips' | 'iaps') (API limitation).
 */
function getActionPlansOfType(uri: string, headers: {}, actionPlanType: 'mcaps' | 'cips' | 'iaps') {
  return ajax.get(uri, headers).pipe(
    expand((res: any) => (res.response.next ? ajax.get(res.response.next, headers) : empty())),
    map((res: AjaxResponse) => {
      return {
        results: res.response.results as RemoteFollowUpData[],
        actionPlanType
      }
    })
  )
}

/**
 * This request is throttled, so prevent follow-ups from having state { fetching: true } unless throttletime has been exceeded.
 */
const syncThrottleEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(throttledSyncFollowUps)),
    // Don't spam network requests
    throttleTime(SYNC_BACKOFF),
    map(syncFollowUps.request)
  )
})

/**
 * The user needs to be shown all action plans for any facility that has at least one overdue (since 2017) or upcoming (within 30 days) action plan.
 */
const syncEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(syncFollowUps.request)),
    // Sync all-or-nothing. If error encountered it halts sync
    exhaustMap(() => {
      const headers = getCommonHeaders(state$);
      const beginningOf2017 = '2017-01-01';
      const thirtyDaysFromNow = moment().add(30, 'd').format('YYYY-MM-DD');
      return from(['mcaps', 'iaps', 'cips'] as ('mcaps' | 'iaps' | 'cips')[]).pipe(
        mergeMap((actionPlanType: 'mcaps' | 'iaps' | 'cips') => {
          // these don't necessarily need to be full, but if they aren't full, there could be an issue if the user has no network.
          const uriBase = `${config.dfdmHost}/api/${actionPlanType}?resolved=false&max=${MAX_FOLLOWUPS_TO_REQUEST_AT_ONCE}&full=true&due=${beginningOf2017}/${thirtyDaysFromNow}&eval_client=true`;
          return getActionPlansOfType(uriBase, headers, actionPlanType);
        }),
        map(prepareActionPlans),
        scan((acc: Record<string, FollowUp[]>, curr: FollowUp[]) => {

          const facilitiesWithPlans: string[] = [];

          curr.forEach((followUp: FollowUp) => {
            facilitiesWithPlans.push(followUp.data.facility);

            if (!acc[followUp.data.facility]) {
              acc[followUp.data.facility] = [followUp];
            } else {
              acc[followUp.data.facility].push(followUp);
            }
          });

          // there are no etags or if-modified-since in use, so subsequent requests will
          // fetch duplicate followups. filter out duplicates.
          new Set(facilitiesWithPlans).forEach(facility => {
            acc[facility] = acc[facility].sort((a: FollowUp, b: FollowUp) => followUpSortComparator(a.data, b.data));
          })

          return acc;
        }, {})
      )
    }),
    debounceTime(2000),
    map((followUps: Record<string, FollowUp[]>) => syncFollowUps.success(followUps)),
    defaultIfEmpty(syncFollowUps.success({})),
    catchError((e) => of(syncFollowUps.failure(e)))
  )
})

const openEditorEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(openFollowUpEditor)),
    map((action) => redirect(`/follow-ups/${action.payload.facilityId}/${action.payload.id}`, action.payload.history))
  );
});

const submitEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(submitFollowUp.request)),
    pluck('payload'),
    map(({ type, id }) => ({
      type,
      id,
      visitData: state$.value.followUps.visits.get(id),
    })),
    switchMap(({ type, id, visitData }) =>
      ajax
        .post(`${config.dfdmHost}/api/${type}s/${id}/visit`, visitData, {
          ...getCommonHeaders(state$),
          [CONTENT_TYPE_HEADER]: 'application/json',
        })
        .pipe(
          map((res) => submitFollowUp.success(res.response)),
          catchError((e) => of(submitFollowUp.failure(e)))
        )
    )
  );
});

/**
 * Instead of eagerly fetching all followups, which is expensive, only fetch all followups for a given facility
 * if that facility is visited. 
 */
const getFollowUpsForFacilityEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(getFollowUpsForFacility.request)),
    switchMap(action => {
      return from(['mcaps', 'iaps', 'cips'] as ('mcaps' | 'iaps' | 'cips')[]).pipe(
        mergeMap((actionPlanType: 'mcaps' | 'iaps' | 'cips') => {
          const headers = getCommonHeaders(state$);
          const uriBase = `${config.dfdmHost}/api/${actionPlanType}?resolved=false&max=${MAX_FOLLOWUPS_TO_REQUEST_AT_ONCE}&full=true&facility=${action.payload}`;

          return getActionPlansOfType(uriBase, headers, actionPlanType);
        }),
        map(prepareActionPlans),
        scan((acc: FollowUp[], curr: FollowUp[]) => {
          return [...acc, ...curr].sort((a: FollowUp, b: FollowUp) => followUpSortComparator(a.data, b.data));
        }, []),
        map((followUps: FollowUp[]) => getFollowUpsForFacility.success({id: action.payload, followUps})),
        catchError((e) => of(getFollowUpsForFacility.failure(e)))
      )
    })
  )
})

export const followUpFacilityFailureEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(getFollowUpsForFacility.failure))
  )
})
export const followUpEpics = combineEpics(syncThrottleEpic, syncEpic, openEditorEpic, submitEpic, getFollowUpsForFacilityEpic);
